import { useEffect, useState ,useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CoverImage from "./CoverImage";
import SimpleLoader from "../../components/LogoLoader";

// Reusable Section Card for premium spacing and shadows
const SectionCard = ({ title, icon, children, className = "" }) => (
  <div
    className={`bg-white p-7 md:p-9 rounded-3xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100/50 ${className}`}
  >
    {title && (
      <h2 className="text-2xl font-bold mb-5 text-[#1f2d1f] flex items-center gap-3">
        {icon && <span>{icon}</span>}
        {title}
      </h2>
    )}
    {children}
  </div>
);

export default function CuratedTripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);



  const fetchTrip = useCallback(async () => {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/curated-trips/${id}`
    );
    const data = await res.json();
    setTrip(data);
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
}, [id]);

useEffect(() => {
  fetchTrip();
}, [fetchTrip]);

  const handleCopy = async () => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) {
      alert("Please login first");
      return;
    }

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/curated-trips/${id}/copy`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await res.json();

    if (!res.ok) {
      alert(data.message);
      return;
    }

    alert("Trip copied 🚀");
  };

  if (loading) {
    return <SimpleLoader />;
  }

  if (!trip) {
    return (
      <div className="min-h-screen pt-24 text-center text-gray-500 text-lg">
        Trip not found
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f6f1] pb-32">
      {/* HERO SECTION */}
      <div className="relative h-[60vh] lg:h-[70vh] bg-gray-900 group overflow-hidden">
        <div className="absolute inset-0">
          <CoverImage city={trip.city} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />

        <div className="absolute top-6 left-4 md:left-8 md:top-24 z-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/90 hover:text-white bg-black/20 hover:bg-black/40 backdrop-blur-md px-5 py-2.5 rounded-full transition-all duration-300 font-medium"
          >
            ← Back
          </button>
        </div>

        <div className="absolute bottom-10 left-6 right-6 md:left-12 max-w-4xl text-white transform transition-all duration-700 ease-out translate-y-0 opacity-100">
          <p className="text-sm font-semibold tracking-widest uppercase text-white/80 mb-3 drop-shadow-sm">
            📍 {trip.city}
          </p>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-8 drop-shadow-lg">
            {trip.title}
          </h1>

          <div
            className="flex items-center gap-4 cursor-pointer group/creator bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/10 px-5 py-3 rounded-2xl transition-all duration-300 inline-flex shadow-lg"
            onClick={() => navigate(`/creator/${trip.creator?._id}`)}
          >
            <div className="relative w-12 h-12 rounded-full overflow-hidden bg-[#f7f6f1] flex items-center justify-center text-[#5b6f00] font-bold shadow-inner">
              {trip.creator?.avatar ? (
                <img
                  src={trip.creator.avatar}
                  alt={trip.creator?.name}
                  className="w-full h-full object-cover group-hover/creator:scale-110 transition-transform duration-500"
                />
              ) : (
                <span className="text-lg">
                  {trip.creator?.name?.[0]?.toUpperCase() || "C"}
                </span>
              )}
            </div>

            <div className="flex flex-col">
              <p className="text-sm text-white font-semibold group-hover/creator:text-white/90 group-hover/creator:underline underline-offset-4 decoration-white/50 transition-all">
                {trip.creator?.name || "Anonymous Creator"}
              </p>
              <p className="text-xs text-white/70 font-medium tracking-wide">
                Travel Creator
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 mt-10 grid lg:grid-cols-3 gap-8 md:gap-12 relative">
        {/* MAIN LINE */}
        <div className="lg:col-span-2 space-y-8 md:space-y-10">
          {/* DESCRIPTION */}
          {trip.description && (
            <SectionCard title="About This Trip">
              <p className="text-gray-600 leading-relaxed text-lg whitespace-pre-line">
                {trip.description}
              </p>
            </SectionCard>
          )}

          {/* INFO GRID */}
          <SectionCard>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-8 gap-x-6">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                  Audience
                </span>
                <span className="font-medium text-gray-800 flex items-center gap-2">
                  🎯 {trip.audience || "Anyone"}
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                  Travel Style
                </span>
                <span className="font-medium text-gray-800 flex items-center gap-2">
                  🧭 {trip.travelStyle || "Mixed"}
                </span>
              </div>
              {trip.bestTime && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                    Best Time
                  </span>
                  <span className="font-medium text-gray-800 flex items-center gap-2">
                    📅 {trip.bestTime}
                  </span>
                </div>
              )}
              {trip.budget > 0 && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                    Budget
                  </span>
                  <span className="font-medium text-gray-800 flex items-center gap-2">
                    💸 ₹{trip.budget.toLocaleString()}
                  </span>
                </div>
              )}
              {trip.duration && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                    Duration
                  </span>
                  <span className="font-medium text-gray-800 flex items-center gap-2">
                    ⏳ {trip.duration} days
                  </span>
                </div>
              )}
              {trip.stay && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                    Stay
                  </span>
                  <span className="font-medium text-gray-800 flex items-center gap-2 capitalize">
                    🏨 {trip.stay}
                  </span>
                </div>
              )}
            </div>
          </SectionCard>

          {/* HIGHLIGHTS */}
          {trip.highlights?.length > 0 && (
            <SectionCard title="Highlights" icon="✨">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {trip.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="mt-2 w-2 h-2 rounded-full bg-[#5b6f00] shrink-0" />
                    <span className="text-gray-700 leading-snug">{h}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* ITINERARY */}
          <div className="pt-6">
            <h2 className="text-3xl font-extrabold mb-10 text-[#1f2d1f]">
              The Itinerary
            </h2>

            {trip.itinerary?.days?.length > 0 ? (
              <div className="relative pl-6 md:pl-10 border-l-[3px] border-[#e5e5e5] space-y-12 py-4">
                {trip.itinerary.days.map((day, index) => (
                  <div key={index} className="relative group">
                    {/* Timeline Dot */}
                    <div className="absolute -left-[45px] md:-left-[61px] top-4 w-11 h-11 rounded-full bg-[#f7f6f1] text-[#5b6f00] border-[3px] border-[#e5e5e5] group-hover:border-[#5b6f00] font-bold flex items-center justify-center text-sm shadow-sm transition-colors duration-300 z-10 box-content">
                      {day.day}
                    </div>

                    <div className="bg-white p-7 md:p-9 rounded-3xl shadow-sm group-hover:shadow-md transition-shadow duration-300 border border-transparent hover:border-[#5b6f00]/20 ml-2 md:ml-4 group-hover:-translate-y-1 transform ease-out">
                      <h3 className="font-bold text-2xl mb-8 text-[#1f2d1f]">
                        Day {day.day}
                      </h3>

                      <div className="space-y-8">
                        {day.morning && (
                          <div className="flex gap-4 items-start">
                            <div className="text-2xl shrink-0 mt-1">🌅</div>
                            <div>
                              <h4 className="font-bold text-sm text-gray-400 uppercase tracking-widest mb-2">
                                Morning
                              </h4>
                              <p className="text-gray-700 leading-relaxed text-lg">
                                {day.morning}
                              </p>
                            </div>
                          </div>
                        )}
                        {day.afternoon && (
                          <div className="flex gap-4 items-start">
                            <div className="text-2xl shrink-0 mt-1">☀️</div>
                            <div>
                              <h4 className="font-bold text-sm text-gray-400 uppercase tracking-widest mb-2">
                                Afternoon
                              </h4>
                              <p className="text-gray-700 leading-relaxed text-lg">
                                {day.afternoon}
                              </p>
                            </div>
                          </div>
                        )}
                        {day.evening && (
                          <div className="flex gap-4 items-start">
                            <div className="text-2xl shrink-0 mt-1">🌙</div>
                            <div>
                              <h4 className="font-bold text-sm text-gray-400 uppercase tracking-widest mb-2">
                                Evening
                              </h4>
                              <p className="text-gray-700 leading-relaxed text-lg">
                                {day.evening}
                              </p>
                            </div>
                          </div>
                        )}
                        {day.tips && (
                          <div className="flex gap-4 bg-[#f7f6f1]/60 p-5 rounded-2xl mt-6 items-start">
                            <div className="text-2xl shrink-0">💡</div>
                            <div className="w-full">
                              <h4 className="font-bold text-xs text-[#5b6f00] uppercase tracking-widest mb-1.5">
                                Insider Tip
                              </h4>
                              <p className="text-[15px] text-gray-600 leading-relaxed">
                                {day.tips}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <SectionCard>
                <div className="text-gray-400 text-center py-10">
                  No itinerary provided.
                </div>
              </SectionCard>
            )}
          </div>

          {/* THINGS TO AVOID */}
          {trip.thingsToAvoid?.length > 0 && (
            <SectionCard title="Things to Avoid" icon="⚠️">
              <ul className="space-y-4">
                {trip.thingsToAvoid.map((t, i) => (
                  <li key={i} className="flex items-start gap-4 text-red-800/80">
                    <div className="mt-2 w-2 h-2 rounded-full bg-red-400/60 shrink-0" />
                    <span className="leading-relaxed text-[17px]">{t}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}

          {/* RECOMMENDATIONS */}
          {trip.recommendations?.length > 0 && (
            <SectionCard title="Good to Know" icon="📌">
              <ul className="space-y-4">
                {trip.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="mt-2 w-2 h-2 rounded-full bg-[#5b6f00]/60 shrink-0" />
                    <span className="leading-relaxed text-gray-700 text-[17px]">{r}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}

          {/* BUDGET DETAILS */}
          {trip.budgetBreakdown && (
            <SectionCard title="Budget Details" icon="💰">
              <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line bg-[#f9fafb] p-6 rounded-2xl border border-gray-100">
                {trip.budgetBreakdown}
              </p>
            </SectionCard>
          )}

          {/* MISC */}
          {trip.misc && (
            <SectionCard title="Extra Notes" icon="🧠">
              <p className="text-gray-700 leading-relaxed text-[17px] whitespace-pre-line">
                {trip.misc}
              </p>
            </SectionCard>
          )}

          {/* TAGS */}
          {trip.tags?.length > 0 && (
            <SectionCard title="Categories" icon="🏷">
              <div className="flex flex-wrap gap-3">
                {trip.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="px-4 py-2 bg-[#f7f6f1] text-[#5b6f00] font-semibold rounded-xl text-sm border border-[#e5e5e5]"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </SectionCard>
          )}
        </div>

        {/* SIDEBAR */}
        <div className="lg:col-span-1 mt-4 lg:mt-0">
          <div className="sticky top-28 bg-white p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col gap-8 group">
            <div>
              <h3 className="text-2xl font-extrabold text-[#1f2d1f] mb-3">
                Love this plan?
              </h3>
              <p className="text-gray-500 text-[15px] leading-relaxed">
                Copy this exact itinerary to your own dashboard. You can customize the dates, add your own notes, and invite friends to collaborate.
              </p>
            </div>

            <button
              onClick={handleCopy}
              className="w-full bg-[#5b6f00] text-white py-4 px-6 rounded-2xl font-bold text-lg hover:bg-[#4a5a00] hover:scale-[1.02] transform transition-all duration-300 shadow-[0_4px_14px_0_rgba(91,111,0,0.39)] hover:shadow-[0_6px_20px_rgba(91,111,0,0.23)] flex items-center justify-center gap-2 active:scale-95"
            >
              Copy Trip to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}